import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Grid,
  styled,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Paper,
  Button,
  Typography
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import PageHeader from '../../../components/PageHeader';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { formatDate } from '../../../util';
import { api } from '../../../api';
import { Person } from '../../../schema/person';

const BlacklistEdit: React.FC = () => {
  const { person_id } = useParams();
  const navigate = useNavigate();

  const [blacklistType, setBlacklistType] = useState<'permanent' | 'temporary'>('permanent');

  const [person, setPerson] = useState<Person | null>(null);

  const [reason, setReason] = useState<string>('');
  const [endDate, setEndDate] = useState<Date | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleDelete = () => {
    if (isLoading) {
      return;
    }
    if (person_id) {
      setIsLoading(true);
      api
        .delete(`blacklist/${person_id}`)
        .then(() => {
          navigate('/blacklist');
        })
        .finally(() => setIsLoading(false));
    }
  };
  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (isLoading) {
      return;
    }
    if (person_id) {
      setIsLoading(true);
      api
        .put(`blacklist/${person_id}`, {
          person_id: person?.id,
          blacklist_end_date:
            blacklistType == 'permanent' ? null : endDate ? formatDate(endDate) : null,
          reason: reason
        })
        .then(() => {
          navigate('/blacklist');
        })
        .finally(() => setIsLoading(false));
    }
  };

  useEffect(() => {
    if (person_id) {
      api.get<Person>(`/person/${person_id}`).then((res) => {
        const { data } = res;
        setPerson(data);
      });
      api.get(`/blacklist/${person_id}`).then((res) => {
        const { data } = res;
        const { blacklist_end_date, reason } = data;
        if (blacklist_end_date) {
          setBlacklistType('temporary');
          setEndDate(new Date(blacklist_end_date));
        }
        if (reason) {
          setReason(reason);
        }
      });
    }
  }, []);

  return (
    <Container>
      <PageHeader text="แก้ไขข้อมูล Blacklist" />
      <Button component={Link} to={`/blacklist`} sx={{ my: 3, p: 0 }}>
        <ArrowBackIosIcon />
        blacklist
      </Button>

      {/* <Typography variant="body2" sx={{ paddingY: 3 }}>
        เพิ่มข้อมูลการขอแลกบัตรล่วงเวลา
      </Typography> */}
      <Paper>
        <Typography variant="h6" sx={{ p: 2 }}>
          แก้ไขข้อมูล
        </Typography>
        <Form onSubmit={handleFormSubmit} sx={{ padding: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="ชื่อ - นามสกุล"
                variant="outlined"
                placeholder="ชื่อ - นามสกุล"
                InputProps={{ readOnly: true }}
                value={person?.full_name ? person.full_name : ''}
                disabled={!person?.full_name}
              />
            </Grid>
            {person ? (
              <>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="บริษัท"
                    variant="outlined"
                    placeholder="บริษัท"
                    InputProps={{ readOnly: true }}
                    value={person?.company?.company_name ? person.company.company_name : ''}
                    disabled={!person?.company?.company_name}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="ประเภทบุคคล"
                    variant="outlined"
                    placeholder="ประเภทบุคคล"
                    InputProps={{ readOnly: true }}
                    value={person?.person_type?.type_name ? person.person_type.type_name : ''}
                    disabled={!person?.person_type?.type_name}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="เบอร์โทรศัพท์"
                    variant="outlined"
                    placeholder="เบอร์โทรศัพท์"
                    InputProps={{ readOnly: true }}
                    value={person?.phone_number ? person.phone_number : ''}
                    disabled={!person?.phone_number}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="อีเมล"
                    variant="outlined"
                    placeholder="อีเมล"
                    InputProps={{ readOnly: true }}
                    value={person?.email ? person.email : ''}
                    disabled={!person?.email}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="ชื่อผู้คุมงาน"
                    variant="outlined"
                    placeholder="ชื่อผู้คุมงาน"
                    InputProps={{ readOnly: true }}
                    value={person?.supervisor?.full_name ? person.supervisor.full_name : ''}
                    disabled={!person?.supervisor?.full_name}
                  />
                </Grid>
              </>
            ) : null}
          </Grid>

          <Grid container spacing={2} mt={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel id="contractor-type">ประเภท Blacklist</InputLabel>
                  <Select
                    labelId="request-type"
                    label="ประเภทการร้องขอ"
                    name="requestType"
                    variant="outlined"
                    onChange={(e) =>
                      setBlacklistType(e.target.value == 'temporary' ? 'temporary' : 'permanent')
                    }
                    value={blacklistType}
                    autoComplete="off"
                  >
                    <MenuItem value="permanent">ถาวร</MenuItem>
                    <MenuItem value="temporary">ชั่วคราว</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <DesktopDatePicker
                  label="วันที่สิ้นสุด Blacklist"
                  inputFormat="MM/dd/yyyy"
                  value={endDate}
                  onChange={(value) => setEndDate(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      disabled={blacklistType == 'permanent'}
                      required={blacklistType == 'temporary'}
                    />
                  )}
                  disabled={blacklistType == 'permanent'}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="สาเหตุ"
                  variant="outlined"
                  placeholder="สาเหตุ"
                  value={reason}
                  minRows={2}
                  multiline
                  onChange={(event) => setReason(event.target.value)}
                />
              </Grid>
            </LocalizationProvider>
          </Grid>
          <Grid container columns={2} spacing={2} direction={'row-reverse'}>
            <Grid item>
              <Button type="submit" variant="contained" color="primary" sx={{ marginTop: 4 }}>
                แก้ไข
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="error"
                sx={{ marginTop: 4 }}
                onClick={handleDelete}
              >
                ลบ
              </Button>
            </Grid>
          </Grid>
        </Form>
      </Paper>
    </Container>
  );
};

export default BlacklistEdit;

const Container = styled('div')`
  padding: 16px;
`;

const Form = styled('form')`
  display: flex;
  flex-direction: column;
  align-items: end;
`;
